import React from 'react';
import { motion } from 'framer-motion';
import SEO from '../components/common/SEO';
import Breadcrumb from '../components/common/Breadcrumb';

function PrivacyPage() {
  const breadcrumbItems = [
    { name: 'Privacy Policy' }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen bg-gray-50 py-8"
    >
      <SEO
        title="Privacy Policy - Pokemon Stats Database"
        description="Privacy Policy for Pokemon Stats Database. Learn how we collect, use, and protect your personal information."
        keywords="privacy policy, data protection, pokemon database privacy, user data"
        url="/privacy"
      />

      <div className="container mx-auto px-4">
        <Breadcrumb items={breadcrumbItems} />

        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="max-w-4xl mx-auto"
        >
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-6">Privacy Policy</h1>
            <p className="text-gray-600 mb-8">
              Last updated: January 1, 2024
            </p>

            <div className="prose max-w-none">
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
                <p className="text-gray-700 mb-4">
                  Pokemon Stats Database ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website pokemonstats.example.com, including any other media form, media channel, mobile website, or mobile application related or connected thereto.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Information We Collect</h2>
                
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Personal Data</h3>
                <p className="text-gray-700 mb-4">
                  Personally identifiable information, such as your name, shipping address, email address, and telephone number, and demographic information, such as your age, gender, hometown, and interests, that you voluntarily give to us when you register with the Service or when you choose to participate in various activities related to the Service, such as online chat and message boards.
                </p>

                <h3 className="text-xl font-semibold text-gray-900 mb-3">Derivative Data</h3>
                <p className="text-gray-700 mb-4">
                  Information our servers automatically collect when you access the Service, such as your IP address, your browser type, your operating system, your access times, and the pages you have viewed directly before and after accessing the Service.
                </p>

                <h3 className="text-xl font-semibold text-gray-900 mb-3">Financial Data</h3>
                <p className="text-gray-700 mb-4">
                  Financial information, such as data related to your payment method (e.g., valid credit card number, card brand, expiration date) that we may collect when you purchase, order, return, exchange, or request information about our services from the Service.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Use of Your Information</h2>
                <p className="text-gray-700 mb-4">
                  Having accurate information about you permits us to provide you with a smooth, efficient, and customized experience. Specifically, we may use information collected about you via the Service to:
                </p>
                <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                  <li>Administer sweepstakes, promotions, and contests</li>
                  <li>Assist law enforcement and respond to subpoenas</li>
                  <li>Compile anonymous statistical data and analysis for use internally or with third parties</li>
                  <li>Create and manage your account</li>
                  <li>Deliver targeted advertising, coupons, newsletters, and promotions</li>
                  <li>Email you regarding your account or order</li>
                  <li>Enable user-to-user communications</li>
                  <li>Fulfill and manage purchases, orders, payments, and other transactions</li>
                  <li>Generate a personal profile about you to make future visits to the Service more personalized</li>
                  <li>Increase the efficiency and operation of the Service</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Disclosure of Your Information</h2>
                <p className="text-gray-700 mb-4">
                  We may share information we have collected about you in certain situations. Your information may be disclosed as follows:
                </p>

                <h3 className="text-xl font-semibold text-gray-900 mb-3">By Law or to Protect Rights</h3>
                <p className="text-gray-700 mb-4">
                  If we believe the release of information about you is necessary to respond to legal process, to investigate or remedy potential violations of our policies, or to protect the rights, property, and safety of others, we may share your information as permitted or required by any applicable law, rule, or regulation.
                </p>

                <h3 className="text-xl font-semibold text-gray-900 mb-3">Business Transfers</h3>
                <p className="text-gray-700 mb-4">
                  We may share or transfer your information in connection with, or during negotiations of, any merger, sale of company assets, financing, or acquisition of all or a portion of our business to another company.
                </p>

                <h3 className="text-xl font-semibold text-gray-900 mb-3">Third-Party Service Providers</h3>
                <p className="text-gray-700 mb-4">
                  We may share your information with third parties that perform services for us or on our behalf, including payment processing, data analysis, email delivery, hosting services, customer service, and marketing assistance.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Tracking Technologies</h2>
                
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Cookies and Web Beacons</h3>
                <p className="text-gray-700 mb-4">
                  We may use cookies, web beacons, tracking pixels, and other tracking technologies on the Service to help customize the Service and improve your experience. When you access the Service, your personal information is not collected through the use of tracking technology.
                </p>

                <h3 className="text-xl font-semibold text-gray-900 mb-3">Internet-Based Advertising</h3>
                <p className="text-gray-700 mb-4">
                  Additionally, we may use third-party software to serve ads on the Service, implement email marketing campaigns, and manage other interactive marketing initiatives.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Third-Party Websites</h2>
                <p className="text-gray-700 mb-4">
                  The Service may contain links to third-party websites and applications of interest, including advertisements and external services, that are not affiliated with us. Once you have used these links to leave the Service, any information you provide to these third parties is not covered by this Privacy Policy.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Security of Your Information</h2>
                <p className="text-gray-700 mb-4">
                  We use administrative, technical, and physical security measures to help protect your personal information. While we have taken reasonable steps to secure the personal information you provide to us, please be aware that despite our efforts, no security measures are perfect or impenetrable.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Policy for Children</h2>
                <p className="text-gray-700 mb-4">
                  We do not knowingly solicit information from or market to children under the age of 13. If we learn that we have collected personal information from a child under age 13 without verification of parental consent, we will delete that information as quickly as possible.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Controls for Do-Not-Track Features</h2>
                <p className="text-gray-700 mb-4">
                  Most web browsers and some mobile operating systems include a Do-Not-Track ("DNT") feature or setting you can activate to signal your privacy preference not to have data about your online browsing activities monitored and collected.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Options Regarding Your Information</h2>
                
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Account Information</h3>
                <p className="text-gray-700 mb-4">
                  You may at any time review or change the information in your account or terminate your account by contacting us using the contact information provided below.
                </p>

                <h3 className="text-xl font-semibold text-gray-900 mb-3">Emails and Communications</h3>
                <p className="text-gray-700 mb-4">
                  If you no longer wish to receive correspondence, emails, or other communications from us, you may opt-out by contacting us or following the unsubscribe instructions provided in the communication.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">11. California Privacy Rights</h2>
                <p className="text-gray-700 mb-4">
                  California Civil Code Section 1798.83, also known as the "Shine The Light" law, permits our users who are California residents to request and obtain from us, once a year and free of charge, information about categories of personal information disclosed to third parties for direct marketing purposes.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Contact Us</h2>
                <p className="text-gray-700 mb-4">
                  If you have questions or comments about this Privacy Policy, please contact us at:
                </p>
                <p className="text-gray-700">
                  Pokemon Stats Database<br />
                  Email: privacy@pokemonstats.example.com<br />
                  Website: <a href="/contact" className="text-blue-600 hover:text-blue-700 transition-colors">Contact Page</a>
                </p>
              </section>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}

export default PrivacyPage;